import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from "react-router-dom";
import axios from "../../utils/axios";
import Swal from 'sweetalert2';
import { getToken, getUser } from '../../utils/Common';
import DataTable from 'react-data-table-component';
import Moment from 'moment';
import Layout from '../../components/Layout';
import { ShoppingCart, Printer, Send, TruckIcon, PackageCheck, AlertCircle, CheckCircle2, Calendar, Filter } from 'lucide-react';

/**
 * Modern Purchase Order Management Component
 * Menampilkan daftar PO dengan status cards interaktif
 */
function PurchaseOrderModern() {
  const token = getToken();
  const headers = { 
    Authorization: `Bearer ${token}`,
    'Access-Control-Allow-Origin': '*', 
    'Content-Type': 'application/json' 
  };
  const history = useHistory();
  const FileDownload = require('js-file-download');
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Session
  const userData = getUser() || {};
  const udeptID = userData.dept_id;
  const hcode = userData.hotel_id;

  // State Management
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [hotelList, setHotelList] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [receiveFilter, setReceiveFilter] = useState('all'); // 'all', 'received', 'not_received'
  const [selectedHotel, setSelectedHotel] = useState('');
  const [loading, setLoading] = useState(false);

  // Date Filter State
  // Default to empty strings to show all data initially, user can set range if needed
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Search 
  const [filtertext, setFilterText] = useState('');
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  // Handle Clear Filters
  const handleResetFilters = () => {
    setFilterText('');
    setStatusFilter(null);
    setReceiveFilter('all');
    setStartDate('');
    setEndDate('');
    setSelectedHotel('');
    setResetPaginationToggle(!resetPaginationToggle);
    getListPO(); // Reload data for all hotels/statuses
  };

  // Get list of hotels
  const getListHotelbyUser = useCallback(() => {
    axios
      .post(`/list/hotel/by-user`, { hcode }, { headers })
      .then(function (response) {
        setHotelList(response.data)
      })
      .catch(function (error) {

      });
  }, [hcode]);

  // Get list of Purchase Orders
  const getListPO = useCallback((hotel) => {
    setLoading(true);
    const hotelCode = hotel || hcode;
    
    axios.post('/waiting/purchase/order', { hcode: hotelCode }, { headers })
      .then(function (response) {

        setPurchaseOrders(Object.values(response.data).flatMap(arr => arr))
      })
      .catch(function (error) { 

        Swal.fire('Error', 'Failed to load purchase orders', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [hcode, headers]);

  // Download PO Excel
  const downloadPurchaseorder = () => {
    axios
      .get(`/generate/excel/report/purchase/order/${hcode}`,
        { responseType: 'blob' },
        { headers }
      )
      .then(function (response) {
        FileDownload(response.data, 'purchase_order.xlsx');
        Swal.fire('Success', 'Excel file downloaded successfully', 'success');
      })
      .catch(function (error) {

        Swal.fire('Error', 'Failed to download file', 'error');
      });
  }

  // Handle status click for filtering
  const handleStatusClick = (status) => {

    setStatusFilter(status);
  };

  // Filter data based on status, search, date range, and receive status
  const getFilteredData = () => {
    let filtered = purchaseOrders;

    // Apply status filter
    if (statusFilter !== null) {
      switch(statusFilter) {
        case 'printed':
          // PO yang belum dicetak (file_po null)
          filtered = filtered.filter(po => !po.file_po);
          break;
        case 'sent':
          // PO yang sudah dikirim (file_po exist but receive_status null)
          filtered = filtered.filter(po => po.file_po && !po.receive_status);
          break;
        case 'partial':
          // PO yang partially received (status 2)
          filtered = filtered.filter(po => po.receive_status == 2);
          break;
        case 'received':
          // PO yang sudah diterima lengkap (status 1)
          filtered = filtered.filter(po => po.receive_status == 1);
          break;
        case 'overdue':
          // PO yang belum ada gambar receive item tapi sudah ada file PO
          filtered = filtered.filter(po => !po.receive_image && po.file_po);
          break;
        default:
          break;
      }
    }

    // Apply Receive Filter
    if (receiveFilter !== 'all') {
      if (receiveFilter === 'received') {
        // Show only received items (Full or Partial)
        filtered = filtered.filter(po => po.receive_status == 1 || po.receive_status == 2 || po.receive_image);
      } else if (receiveFilter === 'not_received') {
        // Show only items not yet received
        filtered = filtered.filter(po => !po.receive_status && !po.receive_image);
      }
    }

    // Apply Date Range Filter
    if (startDate && endDate) {
      filtered = filtered.filter(po => {
        const poDate = Moment(po.created_at);
        return poDate.isBetween(startDate, endDate, 'day', '[]');
      });
    }

    // Apply text search filter
    if (filtertext) {
      filtered = filtered.filter(
        search => Object.values(search).join('').toLowerCase().includes(filtertext.toLowerCase())
      );
    }

    return filtered;
  };

  const filteredItems = getFilteredData();

  const handleClear = () => {
    if (filtertext) {
      setResetPaginationToggle(!resetPaginationToggle);
      setFilterText('');
    }
  };

  // Get status badge color and text
  const getStatusBadge = (row) => {
    if (row.receive_status === 1) {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Received</span>;
    } else if (row.receive_status === 2) {
      return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700">Partial</span>;
    } else if (row.date_po_send) {
      const daysSinceSent = Moment().diff(Moment(row.date_po_send), 'days');
      if (daysSinceSent > 7) {
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">Overdue</span>;
      }
      return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">Sent</span>;
    } else if (row.file_po) {
      return <span className="px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-700">Printed</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">Draft</span>;
  };

  // SubHeader Component for DataTable
  const subHeaderComponent = (
    <div className="flex flex-col w-full space-y-4 py-4">
      {/* Filters Row */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search PO, Item, Supplier..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
              value={filtertext}
              onChange={e => setFilterText(e.target.value)}
            />
            <svg className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>

          {/* Date Range Filter */}
          <div className="flex items-center space-x-2 bg-white border border-gray-300 rounded-lg p-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                <Calendar className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="date"
                className="pl-8 pr-2 py-1 text-sm border-none focus:ring-0 rounded-md text-gray-600"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <span className="text-gray-400">-</span>
            <div className="relative">
              <input
                type="date"
                className="pl-2 pr-2 py-1 text-sm border-none focus:ring-0 rounded-md text-gray-600"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {/* Receive Status Filter */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
              <Filter className="h-4 w-4 text-gray-400" />
            </div>
            <select
              className="pl-8 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white text-sm"
              value={receiveFilter}
              onChange={(e) => setReceiveFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="received">Received</option>
              <option value="not_received">Not Received</option>
            </select>
            <div className="absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Active Filter Indicator */}
          {statusFilter !== null && (
            <div className="flex items-center px-3 py-2 bg-blue-100 rounded-lg">
              <span className="text-xs text-blue-700 font-medium mr-2">
                Status: {
                  statusFilter === 'printed' ? 'Printed' :
                  statusFilter === 'sent' ? 'Sent' :
                  statusFilter === 'partial' ? 'Partial' :
                  statusFilter === 'received' ? 'Received' :
                  statusFilter === 'overdue' ? 'Overdue' : ''
                }
              </span>
              <button
                onClick={() => setStatusFilter(null)}
                className="text-blue-700 hover:text-blue-900"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Hotel Filter */}
          {hotelList && hotelList.length > 0 && (() => {
            const flatHotels = hotelList.flat();
            if (flatHotels.length <= 1) return null;
            
            return (
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                value={selectedHotel}
                onChange={(e) => {
                  setSelectedHotel(e.target.value);
                  getListPO(e.target.value);
                }}
              >
                <option value="">All Hotels</option>
                {flatHotels.map((hotel) => (
                  <option key={hotel.id_hotel} value={hotel.id_hotel}>
                    {hotel.nm_hotel} ({hotel.code_hotel})
                  </option>
                ))}
              </select>
            );
          })()}

          {/* Refresh Button */}
          <button
            onClick={handleResetFilters}
            className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            title="Reset Filters"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Download Button */}
          <button
            onClick={downloadPurchaseorder}
            disabled={loading}
            className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            title="Download Excel"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  // DataTable columns
  const columns = [
    
    {
      name: 'PO No.',
      wrap: true,
      sortable: true,
      cell: (row) => (
        <div className="py-2">
          <div className="font-semibold text-gray-800 break-all">{row.nomer}</div>
        </div>
      ),
    },
    {
      name: 'Origin',
      wrap: true,
      cell: (row) => (
        <div className="py-2 max-h-24 overflow-y-auto">
          <div className="font-semibold text-gray-800">{row.nm_department}</div>
          <div className="text-xs text-gray-500">{row.head}</div>
        </div>
      ),
    },

    {
      name: 'Image',
      width: '120px',
      cell: (row) => {
        return (
          <>
            <div 
              className="flex justify-center cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => row.receive_image && setIsModalOpen(true)}
            >
              {row.receive_image ? (
                <img 
                  src={row.receive_image}
                  alt="Received Item"
                  className="w-16 h-16 object-cover rounded border border-gray-200"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/64?text=No+Image';
                  }}
                />
              ) : (
                <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded border border-gray-200 text-gray-400 text-xs text-center">
                  No Image
                </div>
              )}
            </div>

            {/* Image Modal */}
            {isModalOpen && (
              <div 
                className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
                onClick={() => setIsModalOpen(false)}
              >
                <div 
                  className="relative max-w-4xl w-full max-h-[90vh]"
                  onClick={e => e.stopPropagation()}
                >
                  <button 
                    onClick={() => setIsModalOpen(false)}
                    className="absolute -top-10 right-0 text-white hover:text-gray-300"
                  >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <img 
                    src={row.receive_image}
                    alt="Received Item Full Size"
                    className="max-w-full max-h-[80vh] mx-auto object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
                    }}
                  />
                </div>
              </div>
            )}
          </>
        );
      },
    },
    
    {
      name: 'Item',
      cell: (row) => (
        <div className="py-2 max-h-24 overflow-y-auto">
           <div className="text-xs text-gray-500">
              {row.quotation_total && (
                <span className="text-green-600 ml-1">
                  (IDR {parseInt(row.quotation_total).toLocaleString('id-ID')})
                </span>
              )}
            </div>
          <div className="font-semibold text-gray-800">
            {row.quantity} {row.unit} 
           
          </div>
          <div className="text-xs text-gray-500">{row.nama_item}</div>
        </div>
      ),
      wrap: true,
      sortable: true,
    },
    
    {
      name: 'Est. Receive',
      cell: (row) => (
        <div className="text-sm">
          {Moment(row.item_must_receive).format('DD MMM YYYY')}
        </div>
      ),
    },

    {
      name: 'Need Date',
      width: '120px',
      cell: (row) => (
        <div className="text-sm">
          {Moment(row.date_need).format('DD MMM YYYY')}
        </div>
      ),
      sortable: true,
    },
    {
      name: 'Po Created',
      width: '120px',
      cell: (row) => (
        <div className="text-sm">
          {Moment(row.created_at).format('DD MMM YYYY')}
        </div>
      ),
      sortable: false,
    },
    {
      name: 'Po Sent',
      width: '120px',
      cell: (row) => (
        <div className="text-sm">
          {Moment(row.posent).format('DD MMM YYYY')}
        </div>
      ),
      sortable: false,
    },
    {
      name: 'Status ',
      width: '120px',
      cell: (row) => {
        const getStatusBadge = () => {
          switch(parseInt(row.receive_status)) {
            case 1:
              return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800"> Full Receive</span>;
            case 2:
              return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Half Receive</span>;
            case 3:
              return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800"> Defect Receive</span>;
            case 4:
              return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Reject</span>;
            default:
              return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">Waiting for Received</span>;
          }
        };

      return (
        <div className="flex items-center">
          {getStatusBadge()}
        </div>
      );
    },
    },
    {
      name: 'Doc Status',
      width: '140px',
      cell: (row) => {
        const getPOStatusBadge = () => {
          // Same logic as status cards
          if (!row.file_po) {
            return <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">Waiting for Signed</span>;
          } else if (row.file_po && !row.receive_status) {
            return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">PO Sent</span>;
          } else if (row.receive_status == 1) {
            return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Full Received</span>;
          } else if (row.receive_status == 2) {
            return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Partial Received</span>;
          } else if (!row.receive_image && row.file_po) {
            return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Not Received</span>;
          } else {
            return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">On Progress</span>;
          }
        };

        return (
          <div className="flex items-center">
            {getPOStatusBadge()}
          </div>
        );
      },
    },
    {
      name: 'Actions',
      width: '120px',
      cell: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => history.push(`/detail/proposal/${row.id_pp}`)}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="View Details"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button
            onClick={() => {
              if (row.file_po) {
                // Open PDF in new tab for viewing
                window.open(row.file_po, '_blank');
                
                // Also trigger download
                setTimeout(() => {
                  const link = document.createElement('a');
                  link.href = row.file_po;
                  link.download = `PO_${row.id_po || row.nomer || 'document'}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }, 500); // Small delay to avoid popup blocker issues
              } else {
                Swal.fire('Info', 'PDF file not yet available for this Purchase Order', 'info');
              }
            }}
            className={`p-1 rounded transition-colors ${
              row.file_po 
                ? 'hover:bg-gray-100 cursor-pointer' 
                : 'opacity-50 cursor-not-allowed'
            }`}
            disabled={!row.file_po}
            title={row.file_po ? "View/Download Purchase Order PDF" : "PDF not available"}
          >
            <svg 
              className={`w-4 h-4 ${row.file_po ? 'text-gray-600' : 'text-gray-400'}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
          </button>
        </div>
      ),
    },
    {
      name: 'Action',
      width: '80px',
      center: true,
      cell: row => (
        <div className="flex justify-center">
          <button
            onClick={() => {
              if (row.id_pp) {
                history.push(`/detail/proposal/${row.id_pp}`);
              } else {

              }
            }}
            className="inline-flex items-center justify-center w-8 h-8 text-blue-600 bg-blue-50 rounded-full hover:bg-blue-100 hover:text-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            title="View Details"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  // Custom styles for DataTable - matching Proposal page
  const customStyles = {
    table: {
      style: {
        backgroundColor: 'white',
        borderRadius: '12px',
      },
    },
    tableWrapper: {
      style: {
        display: 'table',
        width: '100%',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#f3f4f6',
        borderBottomWidth: '2px',
        borderBottomColor: '#e5e7eb',
        borderBottomStyle: 'solid',
        minHeight: '52px',
      },
    },
    headCells: {
      style: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#374151',
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingTop: '12px',
        paddingBottom: '12px',
      },
    },
    rows: {
      style: {
        fontSize: '12px',
        color: '#1f2937',
        minHeight: '48px',
        cursor: 'pointer',
        '&:not(:last-of-type)': {
          borderBottomStyle: 'solid',
          borderBottomWidth: '1px',
          borderBottomColor: '#e5e7eb',
        },
        '&:hover': {
          backgroundColor: '#f9fafb',
          cursor: 'pointer',
        },
      },
    },
    cells: {
      style: {
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingTop: '12px',
        paddingBottom: '12px',
      },
    },
    pagination: {
      style: {
        borderTop: '1px solid #e5e7eb',
        minHeight: '56px',
      },
    },
  };

  useEffect(() => {
    getListPO();
    getListHotelbyUser();
  }, []);

  // Calculate statistics
  const stats = {
    total: purchaseOrders.length,
    printed: purchaseOrders.filter(po => !po.file_po).length, // PO belum dicetak (file_po null)
    sent: purchaseOrders.filter(po => po.file_po && !po.receive_status).length, // PO sudah dikirim (file_po exist but receive_status null)
    partial: purchaseOrders.filter(po => po.receive_status == 2).length, // Status 2 = Barang Tidak Full
    received: purchaseOrders.filter(po => po.receive_status == 1).length, // Status 1 = Barang Full Receive
    overdue: purchaseOrders.filter(po => !po.receive_image && po.file_po).length, // Belum ada gambar receive item tapi PO sudah ada
  };

  // Debug logging


  if (purchaseOrders.length > 0) {

  }

  return (
    <Layout title="Purchase Orders" subtitle="Manage and track purchase orders">
      <div className="p-8">
        
        {/* Stats Cards - Individual Status Cards (Clickable to filter) */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          
          {/* Total PO */}
          <div 
            onClick={() => handleStatusClick(null)}
            className={`relative bg-gradient-to-br from-primary-dark to-secondary-dark dark:bg-gray-600 rounded-xl p-3 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 overflow-hidden group ${
              statusFilter === null ? 'ring-2 ring-white ring-offset-2' : ''
            }`}
          >
            <div className="absolute -right-2 opacity-10">
              <ShoppingCart className="w-20 h-20 text-white" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                {statusFilter === null && (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
              <p className="text-xs text-white/70 mt-1">Total</p>
              <p className="text-[10px] text-white/60 mt-0.5">All Orders</p>
            </div>
          </div>

          {/* Printed */}
          <div 
            onClick={() => handleStatusClick('printed')}
            className={`relative bg-gradient-to-br from-secondary-dark to-accent-rose dark:bg-gray-600 rounded-xl p-3 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 overflow-hidden group ${
              statusFilter === 'printed' ? 'ring-2 ring-white ring-offset-2' : ''
            }`}
          >
            <div className="absolute -right-2 opacity-10">
              <Printer className="w-20 h-20 text-white" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-white">{stats.printed}</p>
                {statusFilter === 'printed' && (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
              <p className="text-xs text-white/70 mt-1">Waiting for Signed</p>
              <p className="text-[10px] text-white/60 mt-0.5">Need to Print</p>
            </div>
          </div>

          {/* Sent */}
          <div 
            onClick={() => handleStatusClick('sent')}
            className={`relative bg-gradient-to-br from-accent-rose to-primary-dark dark:bg-gray-600 rounded-xl p-3 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 overflow-hidden group ${
              statusFilter === 'sent' ? 'ring-2 ring-white ring-offset-2' : ''
            }`}
          >
            <div className="absolute -right-2 opacity-10">
              <Send className="w-20 h-20 text-white" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-white">{stats.sent}</p>
                {statusFilter === 'sent' && (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
              <p className="text-xs text-white/70 mt-1">PO Sent</p>
              <p className="text-[10px] text-white/60 mt-0.5">Sent to Supplier</p>
            </div>
          </div>

          {/* Partial */}
          <div 
            onClick={() => handleStatusClick('partial')}
            className={`relative bg-gradient-to-br from-primary-dark to-accent-rose dark:bg-gray-600 rounded-xl p-3 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 overflow-hidden group ${
              statusFilter === 'partial' ? 'ring-2 ring-white ring-offset-2' : ''
            }`}
          >
            <div className="absolute -right-2 opacity-10">
              <TruckIcon className="w-20 h-20 text-white" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-white">{stats.partial}</p>
                {statusFilter === 'partial' && (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
              <p className="text-xs text-white/70 mt-1">Partial</p>
              <p className="text-[10px] text-white/60 mt-0.5">Incomplete</p>
            </div>
          </div>

          {/* Received */}
          <div 
            onClick={() => handleStatusClick('received')}
            className={`relative bg-gradient-to-br from-secondary-dark to-primary-dark dark:bg-gray-600 rounded-xl p-3 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 overflow-hidden group ${
              statusFilter === 'received' ? 'ring-2 ring-white ring-offset-2' : ''
            }`}
          >
            <div className="absolute -right-2 opacity-10">
              <PackageCheck className="w-20 h-20 text-white" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-white">{stats.received}</p>
                {statusFilter === 'received' && (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
              <p className="text-xs text-white/70 mt-1">Received</p>
              <p className="text-[10px] text-white/60 mt-0.5">Complete</p>
            </div>
          </div>

          {/* Overdue */}
          <div 
            onClick={() => handleStatusClick('overdue')}
            className={`relative bg-gradient-to-br from-accent-rose to-secondary-dark dark:bg-gray-600 rounded-xl p-3 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 overflow-hidden group ${
              statusFilter === 'overdue' ? 'ring-2 ring-white ring-offset-2' : ''
            }`}
          >
            <div className="absolute -right-2 opacity-10">
              <AlertCircle className="w-20 h-20 text-white" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-white">{stats.overdue}</p>
                {statusFilter === 'overdue' && (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
              <p className="text-xs text-white/70 mt-1">Not Received</p>
              <p className="text-[10px] text-white/60 mt-0.5">No Image</p>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-xl p-2 shadow-sm overflow-hidden">
          <DataTable
            columns={columns}
            data={filteredItems}
            customStyles={customStyles}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 20, 30, 50]}
            paginationComponentOptions={{
              rowsPerPageText: 'Rows per page:',
              rangeSeparatorText: 'of',
              noRowsPerPage: false,
              selectAllRowsItem: false,
              selectAllRowsItemText: 'All'
            }}
            subHeader
            subHeaderComponent={subHeaderComponent}
            persistTableHead
            responsive
            progressPending={loading}
            progressComponent={
              <div className="flex items-center justify-center p-12">
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600">Loading orders...</p>
                </div>
              </div>
            }
            noDataComponent={
              <div className="flex flex-col items-center justify-center p-12">
                <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="text-gray-500 text-lg mb-2">No orders found</p>
                <p className="text-gray-400 text-sm">Try adjusting your filters or search criteria</p>
              </div>
            }
          />
        </div>
      </div>
    </Layout>
  );
}

export default PurchaseOrderModern;
